// src/components/RegionUtils.js

/**
 * Utility functions for Region Form Components
 * Extracted from RegionForm.js for better maintainability
 */

import { 
    REGION_TYPES, 
    WIDTH_TYPE_MAP, 
    DEFAULT_FORM_DATA, 
    DEFAULT_OPTIONAL_SLICE_DATA,
    OPTIONAL_SLICE_TEMPLATES 
} from './RegionConstants';

/**
 * Get region-specific configuration based on region number
 * @param {string|number} regionNumber - The region number or identifier
 * @returns {Object} Configuration object with ridgeOptionKey and widthType
 */
export const getRegionSpecifics = (regionNumber) => {
    const regionNum = parseInt(regionNumber, 10);
    
    // Check for pterygoid regions (string identifiers)
    if (['right_pterygoid', 'left_pterygoid'].includes(regionNumber)) {
        return { ridgeOptionKey: 'buccopalatal', widthType: 'Buccopalatal' };
    }
    
    // Check numeric regions
    if (WIDTH_TYPE_MAP.BUCCOPALATAL.includes(regionNum)) {
        return { ridgeOptionKey: 'buccopalatal', widthType: 'Buccopalatal' };
    } else if (WIDTH_TYPE_MAP.LABIOPALATAL.includes(regionNum)) {
        return { ridgeOptionKey: 'labiopalatal', widthType: 'Labiopalatal' };
    } else if (WIDTH_TYPE_MAP.BUCCOLINGUAL.includes(regionNum)) {
        return { ridgeOptionKey: 'buccolingual', widthType: 'Buccolingual' };
    } else if (WIDTH_TYPE_MAP.LABIOLINGUAL.includes(regionNum)) {
        return { ridgeOptionKey: 'labiolingual', widthType: 'Labiolingual' };
    }
    
    // Default fallback
    return { ridgeOptionKey: 'buccopalatal', widthType: 'Width' };
};

/**
 * Get the width type for a specific region
 * @param {string|number} regionNumber - The region number or identifier
 * @returns {string} Width type label
 */
export const getWidthType = (regionNumber) => {
    return getRegionSpecifics(regionNumber).widthType;
};

/**
 * Check if a region is in the mandible
 * @param {string} reportType - The report type
 * @param {string|number} regionNumber - The region number or identifier
 * @returns {boolean} True if mandible region
 */
export const isMandibleRegion = (reportType, regionNumber = null) => {
    if (reportType && reportType.includes("Mandible")) {
        return true;
    }
    
    if (regionNumber !== null) {
        const regionNum = parseInt(regionNumber, 10);
        return REGION_TYPES.MANDIBLE_REGIONS.includes(regionNum);
    }
    
    return false;
};

/**
 * Check if a region is in the maxilla
 * @param {string} reportType - The report type
 * @param {string|number} regionNumber - The region number or identifier
 * @returns {boolean} True if maxilla region
 */
export const isMaxillaRegion = (reportType, regionNumber = null) => {
    if (reportType && reportType.includes("Maxilla")) {
        return true;
    }
    
    if (regionNumber !== null) {
        const regionNum = parseInt(regionNumber, 10);
        return REGION_TYPES.MAXILLA_REGIONS.includes(regionNum) || 
               ['right_pterygoid', 'left_pterygoid'].includes(regionNumber);
    }
    
    return false;
};

/**
 * Check if a region should show buccal bone quality options
 * @param {string|number} regionNumber - The region number or identifier
 * @returns {boolean} True if should show buccal options
 */
export const shouldShowBuccal = (regionNumber) => {
    const regionNum = parseInt(regionNumber, 10);
    return REGION_TYPES.BUCCAL_REGIONS.includes(regionNum) || 
           ['right_pterygoid', 'left_pterygoid'].includes(regionNumber);
};

/**
 * Check if a region should show labial bone quality options
 * @param {string|number} regionNumber - The region number or identifier
 * @returns {boolean} True if should show labial options
 */
export const shouldShowLabial = (regionNumber) => {
    const regionNum = parseInt(regionNumber, 10);
    return REGION_TYPES.LABIAL_REGIONS.includes(regionNum);
};

/**
 * Check if a region can have mental foramen slices
 * @param {string|number} regionNumber - The region number or identifier
 * @returns {boolean} True if can have mental foramen slices
 */
export const canHaveMentalForamen = (regionNumber) => {
    const regionNum = parseInt(regionNumber, 10);
    return REGION_TYPES.MENTAL_FORAMEN_REGIONS.includes(regionNum);
};

/**
 * Check if a region can have lingual canal slices
 * @param {string|number} regionNumber - The region number or identifier
 * @returns {boolean} True if can have lingual canal slices
 */
export const canHaveLingualCanal = (regionNumber) => {
    const regionNum = parseInt(regionNumber, 10);
    return REGION_TYPES.LINGUAL_CANAL_REGIONS.includes(regionNum);
};

/**
 * Check if a region can have nasopalatine canal slices
 * @param {string|number} regionNumber - The region number or identifier
 * @returns {boolean} True if can have nasopalatine canal slices
 */
export const canHaveNasopalatine = (regionNumber) => {
    const regionNum = parseInt(regionNumber, 10);
    return REGION_TYPES.NASOPALATINE_REGIONS.includes(regionNum);
};

/**
 * Check if a region can have incisive branch measurements
 * @param {string|number} regionNumber - The region number or identifier
 * @returns {boolean} True if can have incisive branch measurements
 */
export const canHaveIncisiveBranch = (regionNumber) => {
    const regionNum = parseInt(regionNumber, 10);
    return REGION_TYPES.INCISIVE_BRANCH_REGIONS.includes(regionNum);
};

/**
 * Get the appropriate length measurement options for a report type
 * @param {string} reportType - The report type
 * @returns {Array} Array of length measurement options
 */
export const getLengthMeasurementOptions = (reportType) => {
    return isMandibleRegion(reportType) ? 
        require('./RegionConstants').LENGTH_MEASUREMENT_OPTIONS.mandible :
        require('./RegionConstants').LENGTH_MEASUREMENT_OPTIONS.maxilla;
};

/**
 * Initialize form data with defaults and existing data
 * @param {Object} existingData - Existing form data
 * @returns {Object} Merged form data with defaults
 */
export const initializeFormData = (existingData = {}) => {
    return {
        ...DEFAULT_FORM_DATA,
        ...existingData,
        bone_quality: {
            ...DEFAULT_FORM_DATA.bone_quality,
            ...(existingData.bone_quality || {})
        }
    };
};

/**
 * Initialize optional slice data with defaults and existing data
 * @param {Object} existingData - Existing optional slice data
 * @returns {Object} Merged optional slice data with defaults
 */
export const initializeOptionalSliceData = (existingData = {}) => {
    return {
        ...DEFAULT_OPTIONAL_SLICE_DATA,
        ...existingData
    };
};

/**
 * Get default slice content text based on slice type and region
 * @param {string} sliceType - Type of slice (mentalForamen, lingualCanal, nasopalatine)
 * @param {string|number} regionNumber - The region number
 * @returns {string} Default text for the slice
 */
export const getDefaultSliceText = (sliceType, regionNumber) => {
    const templates = OPTIONAL_SLICE_TEMPLATES;
    
    switch (sliceType) {
        case 'mentalForamen':
            if (canHaveMentalForamen(regionNumber)) {
                return templates.mentalForamen;
            }
            break;
        case 'lingualCanal':
            if (canHaveLingualCanal(regionNumber)) {
                return templates.lingualCanal;
            }
            break;
        case 'nasopalatine':
            if (canHaveNasopalatine(regionNumber)) {
                return templates.nasopalatine;
            }
            break;
        default:
            return `Optional ${sliceType.replace('_', ' ')} information for region ${regionNumber}.`;
    }
    
    return '';
};

/**
 * Validate form data completeness
 * @param {Object} formData - Form data to validate
 * @param {string|number} regionNumber - Region number for context
 * @param {string} reportType - Report type for context
 * @returns {Object} Validation result with isValid flag and errors array
 */
export const validateFormData = (formData, regionNumber, reportType) => {
    const errors = [];
    const warnings = [];
    
    // Check critical measurements
    if (!formData.length_crest || formData.length_crest.trim() === '') {
        errors.push('Length from crest measurement is required');
    }
    
    if (!formData.width_crest || formData.width_crest.trim() === '') {
        errors.push('Width measurement is required');
    }
    
    // Check bone quality completeness
    if (!formData.bone_quality.crestal || formData.bone_quality.crestal === '___________') {
        warnings.push('Crestal bone quality not specified');
    }
    
    if (!formData.bone_quality.cancellous || formData.bone_quality.cancellous === '___________') {
        warnings.push('Cancellous bone quality not specified');
    }
    
    // Check region-specific requirements
    if (shouldShowBuccal(regionNumber)) {
        if (!formData.bone_quality.buccal || formData.bone_quality.buccal === '___________') {
            warnings.push('Buccal bone quality not specified');
        }
    }
    
    if (shouldShowLabial(regionNumber)) {
        if (!formData.bone_quality.labial || formData.bone_quality.labial === '___________') {
            warnings.push('Labial bone quality not specified');
        }
    }
    
    if (isMandibleRegion(reportType)) {
        if (!formData.bone_quality.lingual || formData.bone_quality.lingual === '___________') {
            warnings.push('Lingual bone quality not specified');
        }
    }
    
    if (isMaxillaRegion(reportType)) {
        if (!formData.bone_quality.palatal || formData.bone_quality.palatal === '___________') {
            warnings.push('Palatal bone quality not specified');
        }
    }
    
    return {
        isValid: errors.length === 0,
        isComplete: errors.length === 0 && warnings.length === 0,
        errors,
        warnings
    };
};

/**
 * Generate a summary of completed form sections
 * @param {Object} formData - Form data
 * @param {File|null} croppedImageFile1 - Length image file
 * @param {File|null} croppedImageFile2 - Width image file
 * @param {Object} optionalSliceData - Optional slice data
 * @returns {Object} Summary object
 */
export const generateFormSummary = (formData, croppedImageFile1, croppedImageFile2, optionalSliceData) => {
    const hasFormData = Object.values(formData).some(value => {
        if (typeof value === 'string') {
            return value.trim() !== '' && value !== '___' && value !== '___________';
        }
        if (typeof value === 'object' && value !== null) {
            return Object.values(value).some(subValue => 
                subValue !== '___________' && subValue !== '___' && subValue !== ''
            );
        }
        return false;
    });
    
    const hasOptionalSlice = optionalSliceData.sliceImageFile !== null ||
                           optionalSliceData.showMentalForamen ||
                           optionalSliceData.showLingualCanal ||
                           optionalSliceData.showNasopalatine;
    
    return {
        hasLengthImage: !!croppedImageFile1,
        hasWidthImage: !!croppedImageFile2,
        hasOptionalSlice,
        hasFormData,
        completionStatus: hasFormData ? 'In Progress' : 'Not started'
    };
};

/**
 * Create a filename for cropped images
 * @param {string|number} regionNumber - Region number
 * @param {number} imageNumber - Image number (1 or 2)
 * @param {string} suffix - Optional suffix for the filename
 * @returns {string} Generated filename
 */
export const generateImageFilename = (regionNumber, imageNumber, suffix = '') => {
    const baseName = `cropped_image_${regionNumber}_${imageNumber}`;
    return suffix ? `${baseName}_${suffix}.png` : `${baseName}.png`;
};

/**
 * Create a filename for optional slice images
 * @param {string} sliceType - Type of slice
 * @param {string|number} regionNumber - Region number
 * @returns {string} Generated filename
 */
export const generateSliceFilename = (sliceType, regionNumber) => {
    return `optional_slice_${sliceType}_${regionNumber}.png`;
};

/**
 * Clean up blob URLs to prevent memory leaks
 * @param {Array<string>} urls - Array of blob URLs to revoke
 */
export const cleanupBlobUrls = (urls) => {
    urls.forEach(url => {
        if (url && typeof url === 'string' && url.startsWith('blob:')) {
            URL.revokeObjectURL(url);
        }
    });
};

/**
 * Deep clone an object (useful for state management)
 * @param {Object} obj - Object to clone
 * @returns {Object} Cloned object
 */
export const deepClone = (obj) => {
    if (obj === null || typeof obj !== 'object') {
        return obj;
    }
    
    if (obj instanceof Date) {
        return new Date(obj.getTime());
    }
    
    if (obj instanceof Array) {
        return obj.map(item => deepClone(item));
    }
    
    if (typeof obj === 'object') {
        const cloned = {};
        Object.keys(obj).forEach(key => {
            cloned[key] = deepClone(obj[key]);
        });
        return cloned;
    }
    
    return obj;
};